// frontend/src/pages/SubscriptionSuccessPage.tsx
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';
import analyticsService from '../services/analyticsService';
import {
  consumePendingCheckout,
  isPurchaseTracked,
  markPurchaseTracked,
} from '../utils/checkoutTracking';

/** Stripe ersetzt diesen Platzhalter beim Redirect; kommt er unersetzt an, gilt er als fehlend. */
const STRIPE_SESSION_PLACEHOLDER = '{CHECKOUT_SESSION_ID}';

const SubscriptionSuccessPage: React.FC = () => {
  const { isPremium, sellerData, refreshUserData } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [searchParams] = useSearchParams();

  // GA4 `purchase`: feuert genau EINMAL pro Checkout-Rückkehr.
  // Quelle transaction_id = Stripe `session_id` aus der URL; value/currency aus
  // dem sessionStorage-Pending-Record (§6.3/§7). Dreifach-Dedup:
  //   (1) GA4 dedupliziert reporting-seitig über transaction_id,
  //   (2) localStorage-Flag pro session_id (überlebt Reload/Tab-Restore),
  //   (3) Pending-Record wird beim Feuern konsumiert (consume-once).
  useEffect(() => {
    try {
      const rawSessionId = searchParams.get('session_id');
      const sessionId =
        rawSessionId && rawSessionId !== STRIPE_SESSION_PLACEHOLDER ? rawSessionId : null;

      if (!sessionId) {
        // Kein brauchbares session_id. Fallback: nur feuern, wenn ein Record da
        // ist (sonst Direktbesuch/Bookmark → KEIN Event, kein Log-Noise).
        const fallbackRecord = consumePendingCheckout();
        if (!fallbackRecord) {
          return;
        }
        const fallbackTxId = `pending_${fallbackRecord.ts}`;
        if (isPurchaseTracked(fallbackTxId)) {
          return;
        }
        analyticsService.trackPurchase({
          transaction_id: fallbackTxId,
          value: fallbackRecord.value,
          currency: fallbackRecord.currency,
          price_id: fallbackRecord.priceId,
        });
        markPurchaseTracked(fallbackTxId);
        return;
      }

      // Primärpfad mit session_id. Reload-Guard zuerst (Record ist beim
      // Erstfeuern bereits konsumiert, daher kein erneutes Feuern).
      if (isPurchaseTracked(sessionId)) {
        return;
      }
      // Fehlt der Record (Adblocker/Storage), zählt die Conversion trotzdem —
      // dann ohne value (§9).
      const record = consumePendingCheckout();
      analyticsService.trackPurchase({
        transaction_id: sessionId,
        value: record?.value,
        currency: record?.currency,
        price_id: record?.priceId,
      });
      markPurchaseTracked(sessionId);
    } catch (error) {
      logger.error(error, 'purchase tracking failed');
    }
  }, [searchParams]);

  // Prüfung und Anzeige des Abonnement-Status
  useEffect(() => {
    // Versuche die Nutzerdaten zu aktualisieren
    const updateUserData = async () => {
      try {
        await refreshUserData();
        logger.info('user data refreshed after subscription');
      } catch (error) {
        logger.error(error, 'error refreshing user data');
      } finally {
        // Nach 1 Sekunde den Lade-Status beenden, um Feedback zu geben
        setTimeout(() => {
          setIsRefreshing(false);
        }, 1000);
      }
    };
    
    updateUserData();
  }, [refreshUserData]);

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <div className="bg-surface-raised shadow-lg rounded-xl border border-hairline p-8 text-center text-primary">
        <svg 
          className="w-16 h-16 text-accent-ink mx-auto mb-4" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            d="M5 13l4 4L19 7"
          />
        </svg>

        <h1 className="text-3xl font-bold text-accent-ink mb-4">Abonnement erfolgreich!</h1>
        
        <p className="text-lg mb-6 text-secondary">
          Vielen Dank für dein Vertrauen! Dein Premium-Abonnement wurde erfolgreich aktiviert.
        </p>

        {isRefreshing ? (
          <div className="mb-6">
            <p className="text-muted">Aktualisiere Abonnement-Status...</p>
            {/* Optional: Hier einen Spinner einfügen */}
          </div>
        ) : isPremium ? (
          <div className="bg-surface-inset p-4 rounded-md border border-strong mb-6">
            <p className="text-accent-ink">
              <span className="font-semibold">Dein Abonnement ist aktiv!</span>
            </p>
            {sellerData?.planName && (
              <p className="text-primary mt-1">Plan: {sellerData.planName}</p>
            )}
          </div>
        ) : (
          <div className="bg-surface-inset p-4 rounded-md border border-yellow-600 mb-6">
            <p className="text-[var(--tone-warning-fg)]">
              Dein Abonnement wird in Kürze aktiviert. Bitte lade die Seite neu oder kehre später zurück.
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
          <Link 
            to="/dashboard" 
            className="px-6 py-3 bg-indigo-600 text-[#0C1322] rounded-md hover:bg-indigo-700 transition duration-200"
          >
            Zum Dashboard
          </Link>
          <Link 
            to="/dashboard/konto" 
            className="px-6 py-3 bg-surface-inset text-primary rounded-md hover:bg-surface-hover transition duration-200"
          >
            Mein Konto ansehen
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;